/**
 * Generates SEO meta tags for a page
 * @param title - Page title
 * @param description - Page description
 * @param keywords - Comma-separated keywords
 * @param image - Image URL for social sharing
 * @param canonical - Canonical URL to prevent duplicate content
 * @param type - Content type (website, article, etc.)
 * @param author - Content author
 * @param published - Publication date
 * @param readingTime - Estimated reading time
 *
 * Image recommendations:
 * - Open Graph (og:image): 1200x630 pixels (1.91:1 aspect ratio)
 * - Twitter Card (twitter:image): 1200x600 pixels
 * - File formats: JPG, PNG, WEBP
 * - Maximum file size: 5MB
 */
export const seo = ({
  title,
  description,
  keywords,
  image,
  canonical = "https://progressmade.ai",
  type = "website",
  author = "ProgressMade.ai",
  published,
  readingTime,
}: {
  title: string;
  description?: string;
  image?: string;
  keywords?: string;
  canonical?: string;
  type?: string;
  author?: string;
  published?: string;
  readingTime?: string;
}) => {
  const tags = [
    { title },
    { name: "description", content: description },
    { name: "keywords", content: keywords },
    // Twitter/X tags (still using twitter: prefix)
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@ProgressMadeAI" },
    { name: "twitter:site", content: "@ProgressMadeAI" },
    // Open Graph tags
    { name: "og:type", content: type },
    { name: "og:title", content: title },
    { name: "og:description", content: description },
    // Additional modern meta tags
    { name: "theme-color", content: "#6d28d9" },
    { name: "color-scheme", content: "light dark" },
    { name: "robots", content: "index, follow" },
    { name: "author", content: author },
    // Canonical link (proper SEO format)
    { rel: "canonical", href: canonical },
    // Social media specific tags
    { name: "article:author", content: author },
    { name: "article:publisher", content: "ProgressMade.ai" },
    ...(image
      ? [
          { name: "twitter:image", content: image },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "og:image", content: image },
          { name: "og:image:alt", content: `${title} - ProgressMade.ai` },
          { name: "twitter:image:alt", content: `${title} - ProgressMade.ai` },
        ]
      : []),
  ];

  // Add article-specific tags if type is article
  if (type === "article" && published) {
    tags.push({ name: "article:published_time", content: published });

    if (readingTime) {
      tags.push({ name: "article:reading_time", content: readingTime });
    }
  }

  return tags;
};
